import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  AlertTriangle, Shield, Check, X, Eye, Clock, 
  User, Globe, Filter, RefreshCw, CheckCircle, Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

const SEVERITY_CONFIG = {
  critical: { color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50', icon: '🔴' },
  high: { color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50', icon: '🚨' },
  medium: { color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50', icon: '⚠️' },
  low: { color: 'bg-blue-500', textColor: 'text-blue-700', bgColor: 'bg-blue-50', icon: 'ℹ️' }
};

const ALERT_TYPES = {
  MULTIPLE_FAILED_LOGINS: 'Multiple Failed Logins',
  ACCOUNT_LOCKED: 'Account Locked',
  NEW_DEVICE_LOGIN: 'New Device Login',
  NEW_LOCATION_LOGIN: 'New Location Login',
  AFTER_HOURS_ACCESS: 'After Hours Access',
  BULK_DATA_ACCESS: 'Bulk Data Access',
  PRIVILEGE_ESCALATION: 'Privilege Escalation',
  MFA_DISABLED: 'MFA Disabled',
  PASSWORD_CHANGED: 'Password Changed',
  SUSPICIOUS_PHI_ACCESS: 'Suspicious PHI Access'
};

export default function SecurityAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    severity: 'all',
    acknowledged: 'all',
    resolved: 'all'
  });
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAlerts();
    fetchSummary();
  }, [filters]);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.severity !== 'all') params.append('severity', filters.severity);
      if (filters.acknowledged !== 'all') params.append('acknowledged', filters.acknowledged === 'true');
      if (filters.resolved !== 'all') params.append('resolved', filters.resolved === 'true');
      
      const response = await api.get(`/security/alerts?${params.toString()}`);
      setAlerts(response.data.alerts || []);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
      toast.error('Failed to load security alerts');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/security/alerts/summary');
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    setActionLoading(true);
    try {
      await api.post('/security/alerts/acknowledge', { alert_id: alertId });
      toast.success('Alert acknowledged');
      fetchAlerts();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to acknowledge alert');
    } finally {
      setActionLoading(false);
    }
  };

  const resolveAlert = async () => {
    if (!selectedAlert) return;
    
    setActionLoading(true);
    try {
      await api.post('/security/alerts/resolve', { 
        alert_id: selectedAlert.id,
        notes: resolveNotes || 'Resolved without notes'
      });
      toast.success('Alert resolved');
      setSelectedAlert(null);
      setResolveNotes('');
      fetchAlerts();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve alert');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="p-6 space-y-6" data-testid="security-alerts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Bell className="h-7 w-7 text-[#4A238F]" />
            Security Alerts
          </h1>
          <p className="text-slate-600 mt-1">Monitor and respond to security incidents</p>
        </div>
        <Button
          onClick={() => { fetchAlerts(); fetchSummary(); }}
          variant="outline"
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Alerts</p>
                  <p className="text-2xl font-bold text-slate-800">{summary.total}</p>
                </div>
                <div className="p-3 rounded-lg bg-slate-100">
                  <Shield className="h-6 w-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Critical Unresolved</p>
                  <p className="text-2xl font-bold text-red-700">{summary.critical_unresolved}</p>
                </div>
                <div className="p-3 rounded-lg bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">High Unresolved</p>
                  <p className="text-2xl font-bold text-orange-700">{summary.high_unresolved}</p>
                </div>
                <div className="p-3 rounded-lg bg-orange-100">
                  <AlertTriangle className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Unacknowledged</p>
                  <p className="text-2xl font-bold text-amber-700">{summary.unacknowledged}</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-100">
                  <Eye className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-600">Filters:</span>
        </div>
        
        <Select value={filters.severity} onValueChange={(v) => setFilters({ ...filters, severity: v })}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filters.acknowledged} onValueChange={(v) => setFilters({ ...filters, acknowledged: v })}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Acknowledged" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="false">Unacknowledged</SelectItem>
            <SelectItem value="true">Acknowledged</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filters.resolved} onValueChange={(v) => setFilters({ ...filters, resolved: v })}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Resolved" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="false">Unresolved</SelectItem>
            <SelectItem value="true">Resolved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400" />
            <p className="mt-2 text-slate-500">Loading alerts...</p>
          </div>
        ) : alerts.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-medium text-slate-800">No alerts found</h3>
              <p className="text-slate-500">No security alerts match your current filters.</p>
            </CardContent>
          </Card>
        ) : (
          alerts.map((alert) => {
            const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.medium;
            return (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className={`border-l-4 ${config.bgColor}`} style={{ borderLeftColor: config.color.replace('bg-', '').includes('red') ? '#ef4444' : config.color.replace('bg-', '').includes('orange') ? '#f97316' : config.color.replace('bg-', '').includes('amber') ? '#f59e0b' : '#3b82f6' }}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg">{config.icon}</span>
                          <h3 className="font-semibold text-slate-800">
                            {ALERT_TYPES[alert.alert_type] || alert.alert_type}
                          </h3>
                          <Badge variant="outline" className={config.textColor}>
                            {alert.severity.toUpperCase()}
                          </Badge>
                          {alert.resolved && (
                            <Badge className="bg-green-500">Resolved</Badge>
                          )}
                          {!alert.acknowledged && !alert.resolved && (
                            <Badge variant="secondary" className="bg-amber-100 text-amber-700">New</Badge>
                          )}
                        </div>
                        
                        <p className="text-slate-600 mb-3">{alert.description}</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {formatDate(alert.created_at)}
                          </span>
                          {alert.user_email && (
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              {alert.user_email}
                            </span>
                          )}
                          {alert.ip_address && (
                            <span className="flex items-center gap-1">
                              <Globe className="h-4 w-4" />
                              {alert.ip_address}
                            </span>
                          )}
                        </div>
                        
                        {alert.resolved && (
                          <div className="mt-3 p-2 bg-green-50 rounded text-sm">
                            <p className="text-green-700">
                              <strong>Resolved by:</strong> {alert.resolved_by_name} on {formatDate(alert.resolved_at)}
                            </p>
                            {alert.resolution_notes && (
                              <p className="text-green-600 mt-1">{alert.resolution_notes}</p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        {!alert.acknowledged && !alert.resolved && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => acknowledgeAlert(alert.id)}
                            disabled={actionLoading}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Acknowledge
                          </Button>
                        )}
                        {!alert.resolved && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => setSelectedAlert(alert)}
                            disabled={actionLoading}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Resolve Dialog */}
      <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Security Alert</DialogTitle>
            <DialogDescription>
              Add notes about how this alert was resolved.
            </DialogDescription>
          </DialogHeader>
          
          {selectedAlert && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">
                  {ALERT_TYPES[selectedAlert.alert_type] || selectedAlert.alert_type}
                </p>
                <p className="text-sm text-slate-600">{selectedAlert.description}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Resolution Notes</Label>
                <Textarea
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  placeholder="Describe the actions taken to resolve this alert..."
                  rows={4}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedAlert(null)}>
              Cancel
            </Button>
            <Button 
              onClick={resolveAlert}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700"
            >
              {actionLoading ? 'Resolving...' : 'Mark as Resolved'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
